import { debounce } from "lodash"
import {
	init,
	track,
	type PlausibleRequestPayload,
} from "@plausible-analytics/tracker"

// Env variables
import { GTM_CONTAINER_ID, PLAUSIBLE_DOMAIN } from "@/lib/env"
import { featureFlags } from "@/lib/featureFlags"

// Plausible tracking is gated behind a feature flag (default off). Read once at
// module load; flag values are static so this can't change at runtime. Mirrors
// the inline lookup used by `useFeatureFlags` in "@/context/user".
const PLAUSIBLE_ENABLED = Boolean(
	featureFlags.find((flag) => flag.name === "ENABLE_PLAUSIBLE_ANALYTICS")
		?.value,
)

export const generateSendEventDebounced = (
	limit = 1000,
): ReturnType<typeof debounce<typeof sendEvent>> => {
	return debounce(sendEvent, limit)
}

// Collapse dynamic route segments (IDs, dates, UUIDs, page numbers) so the
// Top Pages report shows section-level entries instead of a long tail of
// permalinks. Cardinality only — the tracker dedupes SPA pageviews on the
// raw pathname before transformRequest runs, so this does not by itself
// reduce billable event volume.
const DYNAMIC_SEGMENT_PATTERNS: RegExp[] = [
	/^\d+$/, // numeric ids, page numbers
	/^\d{4}-\d{2}(-\d{2})?$/, // YYYY-MM or YYYY-MM-DD
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, // uuid
]

const collapsePathname = (pathname: string): string =>
	pathname
		.split("/")
		.map((segment) =>
			DYNAMIC_SEGMENT_PATTERNS.some((re) => re.test(segment))
				? ":id"
				: segment,
		)
		.join("/")

// Hard cap on autocaptured pageviews per browsing session. Sessions average
// ~7 pageviews; capping at 5 trims the tail without losing the entry/first-
// interaction picture. Custom sendEvent() calls are unaffected.
const PAGEVIEW_CAP_PER_SESSION = 5
const SESSION_PAGEVIEW_KEY = "plausible_pv_count"

// Suppresses Plausible for pages where GTM is also disabled (investor test /
// suitability flow). Toggled by enablePlausible() to mirror
// enableGoogleTagManager().
let plausibleSuppressed = false

const shouldDropPageview = (payload: PlausibleRequestPayload): boolean => {
	// Only cap pageviews; custom events are low-volume, high-signal.
	if (payload.n !== "pageview") return false

	if (plausibleSuppressed) return true

	// The `/` route just redirects into either /investments/projects or
	// /stats/platform via <Navigate> (see App.tsx). Two pageviews fire per
	// entry; drop the transient root one.
	try {
		if (new URL(payload.u).pathname === "/") return true
	} catch {
		// fall through
	}

	try {
		const raw = window.sessionStorage.getItem(SESSION_PAGEVIEW_KEY)
		const count = raw ? Number.parseInt(raw, 10) : 0
		if (Number.isFinite(count) && count >= PAGEVIEW_CAP_PER_SESSION) {
			return true
		}
		window.sessionStorage.setItem(
			SESSION_PAGEVIEW_KEY,
			String((Number.isFinite(count) ? count : 0) + 1),
		)
	} catch {
		// sessionStorage unavailable (private mode / SSR / disabled) — send.
	}
	return false
}

// Initialise Plausible once at module load. Same-origin proxy via vercel.json
// keeps the events endpoint first-party (avoids ad-blockers).
if (typeof window !== "undefined" && PLAUSIBLE_DOMAIN && PLAUSIBLE_ENABLED) {
	init({
		domain: PLAUSIBLE_DOMAIN,
		endpoint: "/plausible-api/event",
		autoCapturePageviews: true,
		outboundLinks: false,
		fileDownloads: false,
		transformRequest: (payload) => {
			// Returning a falsy value cancels the event in the tracker.
			if (shouldDropPageview(payload)) return null
			try {
				const url = new URL(payload.u)
				url.pathname = collapsePathname(url.pathname)
				url.search = ""
				url.hash = ""
				payload.u = url.toString()
			} catch {
				// If URL parsing fails, send the event through unchanged.
			}
			return payload
		},
	})
}

// Fires the same event to GA (gtag) and Plausible.
// - GA:        https://developers.google.com/analytics/devguides/collection/gtagjs/events
// - Plausible: https://plausible.io/docs/custom-event-goals
export const sendEvent = (category: string, action: string, args?: any) => {
	if (typeof window.gtag !== "undefined") {
		window.gtag("event", category, {
			action,
			...args,
		})
	}

	if (PLAUSIBLE_DOMAIN && PLAUSIBLE_ENABLED && !plausibleSuppressed) {
		// Plausible requires string-valued props.
		const props: Record<string, string> = { action }
		for (const [key, value] of Object.entries(args ?? {})) {
			props[key] = String(value)
		}
		track(category, { props })
	}
}

/**
 * Suppress Plausible tracking (pageviews + custom events) on pages that also
 * disable GTM via enableGoogleTagManager(). Kept as a separate export so
 * consumers can call it alongside enableGoogleTagManager to fully honour the
 * "no tracking on this page" intent.
 */
export const enablePlausible = (enabled: boolean) => {
	plausibleSuppressed = !enabled
}

// These types are specifically for privacy-related settings and do not exist on @types/gtag
type GtagSetOptions = {
	allow_google_signals: boolean
	allow_ad_personalization_signals: boolean
	restricted_data_processing: boolean
	send_page_view: boolean
}

/**
 * Disable GTM / GA
 * DOCS: https://developers.google.com/tag-platform/security/guides/privacy#gtag.js_1
 */
export const enableGoogleTagManager = (enabled: boolean) => {
	const w = window as any

	// GTM may be blocked by ad/tracker blockers — bail out silently if so
	if (typeof window.gtag === "undefined") {
		return
	}

	if (enabled === false) {
		// Should stop GA from all tracking
		w[`ga-disable-${GTM_CONTAINER_ID}`] = true

		// Disabled specific tracking features
		window.gtag("set", { allow_google_signals: false } as GtagSetOptions)
		window.gtag("set", {
			allow_ad_personalization_signals: false,
		} as GtagSetOptions)
		window.gtag("set", {
			restricted_data_processing: true,
		} as GtagSetOptions)
		window.gtag("set", { send_page_view: false } as GtagSetOptions)
	}

	// Re-enable
	if (enabled === true) {
		w[`ga-disable-${GTM_CONTAINER_ID}`] = false
		window.gtag("set", { allow_google_signals: true } as GtagSetOptions)
		window.gtag("set", {
			allow_ad_personalization_signals: true,
		} as GtagSetOptions)
		window.gtag("set", {
			restricted_data_processing: false,
		} as GtagSetOptions)
		window.gtag("set", { send_page_view: true } as GtagSetOptions)
	}
}

export const GA_EVENTS = {
	FORM_SUBMIT_CONTACT: "form_submit_contact",
	BUTTON_CLICK_SIGNUP: "button_click_signup",
	VIDEO_PLAY_TUTORIAL: "video_play_tutorial",
} as const
export type GAEventName = (typeof GA_EVENTS)[keyof typeof GA_EVENTS]
